import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ShieldCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const inputCls = "bg-[#03161D] border border-[#0e4555] text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#0dd3ce] focus-visible:border-[#0dd3ce] rounded-none h-11";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate("/admin", { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#062632] grid md:grid-cols-2">
      <div className="relative hidden md:flex flex-col justify-between p-12 border-r border-[#0a3a47] bg-[#03161D] overflow-hidden">
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-[#0dd3ce]/10 blur-[120px] rounded-full" />
        <div className="relative">
          <div className="flex items-center gap-3 mb-12">
            <img src="/brand/a3-sagar-logo-transparent.png" alt="A3 Sagar Polymers" className="h-12 w-auto" style={{ filter: "brightness(2.4) saturate(1.1)" }} />
            <div className="text-slate-50 font-display font-bold text-lg tracking-tight">A3 SAGAR POLYMERS</div>
          </div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#0dd3ce] font-semibold mb-4">Internal Team Portal</div>
          <h1 className="font-display text-4xl font-bold text-slate-100 leading-tight tracking-tight">Manage every B2B inquiry from a single industrial control room.</h1>
          <p className="text-slate-400 mt-5 leading-relaxed max-w-md">Track RFQs through every stage — from new inquiry to sample production to completed delivery. Built for our Mumbai team.</p>
        </div>
        <div className="text-xs text-slate-500 relative">Restricted access · A3 Sagar staff only</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} data-testid="login-form" className="w-full max-w-md border border-[#0a3a47] bg-[#0a3845] p-10">
          <div className="flex items-center gap-2 text-[#0dd3ce] mb-2">
            <Lock className="w-4 h-4" />
            <span className="text-[10px] uppercase tracking-[0.3em] font-semibold">Secure Login</span>
          </div>
          <h2 className="font-display text-3xl font-bold text-slate-100">Team Sign-In</h2>
          <p className="text-sm text-slate-500 mt-2">Enter your A3 Sagar credentials to continue.</p>

          <div className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.18em] text-slate-400 font-semibold">Email</Label>
              <Input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="admin@a3sagar.com" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.18em] text-slate-400 font-semibold">Password</Label>
              <Input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" />
            </div>
          </div>

          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading}
            className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#0dd3ce] text-[#03161D] font-semibold px-6 py-3 text-sm uppercase tracking-wider hover:bg-[#5ce7e3] disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign In"} <ArrowRight className="w-4 h-4" />
          </button>

          <div className="text-[11px] text-slate-500 mt-6 text-center">
            For team access only. Contact ownership for credentials.
          </div>
        </form>
      </div>
    </div>
  );
}
